import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useListingStore } from '../store/listingStore';
import { getSettings, triggerPriceResearch, updateListing } from '../lib/api';
import { getDevPin, clearDevPin } from '../lib/devPins';
import { useStepAction } from '../hooks/useStepAction';
import clsx from 'clsx';

// ── Animated pricing progress ─────────────────────────────────────────────────
const PRICING_STAGES = [
  { delay: 0,     pct: 8,  text: 'Scanning eBay sold listings…' },
  { delay: 2500,  pct: 28, text: 'Gathering recent sale prices…' },
  { delay: 6000,  pct: 48, text: 'Analysing condition & category…' },
  { delay: 11000, pct: 66, text: 'Comparing market trends…' },
  { delay: 17000, pct: 82, text: 'Calculating suggested price…' },
  { delay: 24000, pct: 93, text: 'Finalising research…' },
];

function usePricingProgress(active: boolean) {
  const [stageIdx, setStageIdx] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    if (!active) { timersRef.current.forEach(clearTimeout); setStageIdx(0); return; }
    timersRef.current = PRICING_STAGES.map((s, i) => setTimeout(() => setStageIdx(i), s.delay));
    return () => timersRef.current.forEach(clearTimeout);
  }, [active]);
  return PRICING_STAGES[stageIdx] ?? PRICING_STAGES[PRICING_STAGES.length - 1];
}

function PricingProgressCard({ pct, text }: { pct: number; text: string }) {
  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex items-center gap-3">
        <div className="relative w-8 h-8 flex-shrink-0">
          <div className="absolute inset-0 rounded-full border-[3px] border-blue-100" />
          <div className="absolute inset-0 rounded-full border-[3px] border-blue-500 border-t-transparent animate-spin" />
        </div>
        <p className="text-sm font-semibold text-blue-700 animate-pulse">{text}</p>
      </div>
      <div className="space-y-0.5">
        <div className="w-full h-1.5 bg-blue-100 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-blue-400 text-right">{pct}%</p>
      </div>
    </div>
  );
}

// ── Shipping constants ────────────────────────────────────────────────────────
const SHIPPING_SERVICES = [
  'USPS First Class (under 1 lb)',
  'USPS Priority Mail',
  'USPS Priority Mail Express',
  'UPS Ground',
  'UPS 2-Day Air',
  'FedEx Ground',
  'FedEx 2Day',
  'Freight (large items)',
  'Local pickup only',
  "Free shipping (I'll build it into the price)",
];

const HANDLING_TIMES = ['Same day', '1 business day', '2 business days', '3 business days', '5 business days'];
const RETURN_WINDOWS = [14, 30, 60];

// ── Main component ────────────────────────────────────────────────────────────
export default function Step5PriceShipping() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useListingStore();
  const [ebaySearchQuery, setEbaySearchQuery] = useState('');
  const [editingSearch, setEditingSearch] = useState(false);
  const [usingPin, setUsingPin] = useState(false);
  const [aiFilledService, setAiFilledService] = useState<string | null>(null);

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings });

  useEffect(() => {
    setEbaySearchQuery(store.identification?.identification || '');
  }, [store.identification]);

  // Trigger pricing research
  useEffect(() => {
    const pin = getDevPin(5);
    if (pin && store.pricingJobStatus === 'PENDING') {
      store.setPricingResearch(pin.pricingResearch);
      if (pin.suggestedPrice) store.setFinalPrice(pin.suggestedPrice);
      store.setPricingJobStatus('COMPLETE');
      setUsingPin(true);
      return;
    }
    if (id && store.pricingJobStatus === 'PENDING') {
      store.setPricingJobStatus('QUEUED');
      triggerPriceResearch(id).catch(() => store.setPricingJobStatus('FAILED'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-fill price
  useEffect(() => {
    if (settings?.autoFillSuggestedPrice && store.pricingResearch?.suggestedPrice && !store.finalPrice) {
      store.setFinalPrice(String(store.pricingResearch.suggestedPrice));
    }
  }, [settings, store.pricingResearch, store.finalPrice]);

  // Auto-fill shipping suggestion
  useEffect(() => {
    const suggestion = store.shippingSuggestion;
    if (!store.shippingService && suggestion && settings?.autoFillShipping) {
      store.setShippingService(suggestion.recommendedService);
      store.setShippingCost(String(suggestion.estimatedCost));
      store.setHandlingTime(suggestion.handlingTime);
    }
  }, [settings, store.shippingSuggestion]);

  const isLoading = store.pricingJobStatus === 'QUEUED' || store.pricingJobStatus === 'PROCESSING';
  const isQueuing = store.pricingJobStatus === 'PENDING';
  const pricing = store.pricingResearch;
  const { pct, text } = usePricingProgress(isLoading);
  const isFreeShipping = store.shippingService === "Free shipping (I'll build it into the price)";
  const suggestion = store.shippingSuggestion;

  const needsBIN = store.listingType === 'BUY_IT_NOW' || store.listingType === 'AUCTION_BIN';
  const needsBid = store.listingType === 'AUCTION' || store.listingType === 'AUCTION_BIN';
  const canProceed =
    (!needsBIN || !!store.finalPrice) &&
    (!needsBid || !!store.startingBid) &&
    !!store.shippingService &&
    (isFreeShipping || !!store.shippingCost);

  useStepAction('Next: Title →', !canProceed, handleNext);

  async function handleNext() {
    if (!id || !canProceed) return;
    await updateListing(id, {
      finalPrice: store.finalPrice ? parseFloat(store.finalPrice) : undefined,
      suggestedPrice: pricing?.suggestedPrice,
      listingType: store.listingType,
      startingBid: store.startingBid ? parseFloat(store.startingBid) : undefined,
      auctionDuration: store.auctionDuration,
      shippingService: store.shippingService,
      shippingCost: isFreeShipping ? 0 : parseFloat(store.shippingCost),
      handlingTime: store.handlingTime,
      acceptReturns: store.acceptReturns,
      returnWindow: store.acceptReturns ? store.returnWindow : undefined,
    });
    store.setCurrentStep(6);
    navigate(`/listing/${id}/step/6`);
  }

  function buildEbayUrl() {
    return `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(ebaySearchQuery)}&LH_Complete=1&LH_Sold=1`;
  }

  // Total cost to buyer
  const binPrice = parseFloat(store.finalPrice) || 0;
  const shipCost = isFreeShipping ? 0 : parseFloat(store.shippingCost) || 0;
  const bidPrice = parseFloat(store.startingBid) || 0;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* ── Page header ── */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Price &amp; Shipping</h2>
        <p className="text-gray-500 mt-1">Review the market research, then set your price and shipping.</p>
      </div>

      {usingPin && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <span className="text-sm text-green-700"><strong>Pinned data</strong> — skipped pricing API</span>
          <button onClick={() => { clearDevPin(5); setUsingPin(false); store.setPricingJobStatus('PENDING'); store.setPricingResearch(null); if (id) triggerPriceResearch(id).catch(() => {}); }}
            className="text-xs text-green-600 hover:text-green-800 underline">Run live</button>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* SECTION 1: Market Research                                          */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <header className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-base">Market research</h3>
              <p className="text-xs text-gray-500">Suggested Buy It Now price, based on recent sold listings</p>
            </div>
          </div>
        </header>

        <div className="p-6">
          {isQueuing && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
              Queuing research…
            </div>
          )}
          {isLoading && <PricingProgressCard pct={pct} text={text} />}

          {!isLoading && !isQueuing && pricing && (
            <div className="grid grid-cols-3 gap-6">
              {/* Hero suggested price */}
              <div className="col-span-1 flex flex-col">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Suggested Buy It Now price</p>
                <p className="text-4xl font-bold text-gray-900 leading-none">${pricing.suggestedPrice.toFixed(2)}</p>
                {pricing.priceRange && (
                  <p className="text-xs text-gray-400 mt-2">
                    Range: <span className="text-gray-600 font-medium">${pricing.priceRange.low}</span> – <span className="text-gray-600 font-medium">${pricing.priceRange.high}</span>
                  </p>
                )}
                <button
                  onClick={() => store.setFinalPrice(String(pricing.suggestedPrice))}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Use as Buy It Now price
                </button>

                {/* eBay search link */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {editingSearch ? (
                    <div className="flex items-center gap-1.5">
                      <input value={ebaySearchQuery} onChange={(e) => setEbaySearchQuery(e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500" />
                      <button onClick={() => setEditingSearch(false)} className="text-xs text-gray-400">Done</button>
                      <a href={buildEbayUrl()} target="_blank" rel="noopener noreferrer"
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">Go</a>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <a href={buildEbayUrl()} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex-1 truncate inline-flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Verify on eBay
                      </a>
                      <button onClick={() => setEditingSearch(true)} className="text-gray-400 hover:text-gray-600" title="Edit search query">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Reasoning + Sources */}
              <div className="col-span-2 space-y-4">
                {/* Reasoning */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Why this price</p>
                  <ul className="space-y-1.5">
                    {pricing.reasoning.map((r, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Sources */}
                {pricing.sources?.filter((s) => s.url).length ? (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Sources ({pricing.sources!.filter((s) => s.url).length})</p>
                    <ul className="space-y-1 max-h-32 overflow-y-auto pr-2">
                      {pricing.sources!.filter((s) => s.url).map((src, i) => (
                        <li key={i}>
                          <a href={src.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-start gap-2 group rounded hover:bg-gray-50 p-1 -mx-1">
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-gray-700 group-hover:text-blue-600 truncate">
                                {src.price && <span className="font-semibold">${src.price} — </span>}
                                {src.title || new URL(src.url).hostname}
                              </p>
                              <p className="text-[10px] text-gray-400 truncate">{new URL(src.url).hostname}</p>
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {!isLoading && !isQueuing && !pricing && store.pricingJobStatus === 'FAILED' && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              Research failed — enter a price manually below.
            </div>
          )}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* SECTION 2: Your decisions — Listing type + Price | Shipping        */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-6">

        {/* ── Card: Listing type & price ── */}
        <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">Your price</h3>
                <p className="text-xs text-gray-500">How buyers see this listing</p>
              </div>
            </div>
          </header>

          <div className="p-6 space-y-5 flex-1">
            {/* Listing type selector */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Listing type</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'BUY_IT_NOW',  label: 'Buy It Now', hint: 'Fixed price' },
                  { value: 'AUCTION',     label: 'Auction',    hint: 'Bidding only' },
                  { value: 'AUCTION_BIN', label: 'Both',       hint: 'Bid or buy now' },
                ] as const).map(({ value, label, hint }) => (
                  <button key={value} onClick={() => store.setListingType(value)}
                    className={clsx(
                      'rounded-xl border-2 py-2.5 px-2 text-center transition-all',
                      store.listingType === value
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300',
                    )}
                  >
                    <p className={clsx('text-sm font-semibold', store.listingType === value ? 'text-blue-700' : 'text-gray-700')}>{label}</p>
                    <p className={clsx('text-[10px] mt-0.5', store.listingType === value ? 'text-blue-500' : 'text-gray-400')}>{hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Buy It Now price */}
            {(store.listingType === 'BUY_IT_NOW' || store.listingType === 'AUCTION_BIN') && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Buy It Now price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-base">$</span>
                  <input type="number" min="0" step="0.01" value={store.finalPrice}
                    onChange={(e) => store.setFinalPrice(e.target.value)} placeholder="0.00"
                    className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2.5 text-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                {pricing && parseFloat(store.finalPrice) !== pricing.suggestedPrice && (
                  <button onClick={() => store.setFinalPrice(String(pricing.suggestedPrice))}
                    className="text-xs text-blue-600 hover:underline">
                    Use suggested: ${pricing.suggestedPrice.toFixed(2)}
                  </button>
                )}
              </div>
            )}

            {/* Starting bid */}
            {(store.listingType === 'AUCTION' || store.listingType === 'AUCTION_BIN') && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Starting bid <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-base">$</span>
                  <input type="number" min="0" step="0.01" value={store.startingBid}
                    onChange={(e) => store.setStartingBid(e.target.value)} placeholder="0.00"
                    className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2.5 text-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                {pricing && (() => {
                  const rec = pricing.priceRange?.low ?? Math.round(pricing.suggestedPrice * 0.65 * 100) / 100;
                  return (
                    <button onClick={() => store.setStartingBid(String(rec))}
                      className="text-xs text-blue-600 hover:underline">
                      Use suggested: ${rec.toFixed(2)}
                    </button>
                  );
                })()}
              </div>
            )}

            {/* Auction duration */}
            {(store.listingType === 'AUCTION' || store.listingType === 'AUCTION_BIN') && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Auction duration</label>
                <div className="flex gap-1.5">
                  {[1, 3, 5, 7, 10].map((d) => (
                    <button key={d} onClick={() => store.setAuctionDuration(d)}
                      className={clsx('flex-1 py-2 rounded-lg border-2 text-xs font-semibold transition-colors',
                        store.auctionDuration === d
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300',
                      )}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Card: Shipping ── */}
        <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l9-4 9 4M3 7l9 4 9-4M3 7v10l9 4 9-4V7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">Shipping &amp; returns</h3>
                <p className="text-xs text-gray-500">How and when buyers get the item</p>
              </div>
            </div>
          </header>

          <div className="p-6 space-y-5 flex-1">
            {/* Shipping service */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Service <span className="text-red-500">*</span>
                </label>
                {store.shippingSuggestionStatus === 'LOADING' && !suggestion && (
                  <span className="text-xs text-blue-500 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    AI estimating…
                  </span>
                )}
              </div>
              {suggestion && (
                <div className="flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-md px-2 py-1">
                  <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
                  </svg>
                  <span><span className="font-semibold">AI-estimated cost</span> auto-fills per service</span>
                </div>
              )}
              <select value={store.shippingService}
                onChange={(e) => {
                  const svc = e.target.value;
                  store.setShippingService(svc);
                  if (svc === "Free shipping (I'll build it into the price)") {
                    store.setShippingCost('0'); setAiFilledService(null);
                  } else if (suggestion?.costEstimates?.[svc] != null) {
                    store.setShippingCost(String(suggestion.costEstimates[svc]));
                    setAiFilledService(svc);
                  } else {
                    setAiFilledService(null);
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="">Select a service…</option>
                {SHIPPING_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Shipping cost + handling on one row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Cost <span className="text-red-500">*</span></label>
                  {aiFilledService && store.shippingService === aiFilledService && !isFreeShipping && (
                    <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-1.5 py-0.5">AI</span>
                  )}
                </div>
                {isFreeShipping ? (
                  <div className="border border-green-300 bg-green-50 rounded-lg px-3 py-2.5 text-green-700 text-sm font-semibold text-center">FREE</div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input type="number" min="0" step="0.01" value={store.shippingCost}
                      onChange={(e) => { store.setShippingCost(e.target.value); setAiFilledService(null); }}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Ships within</label>
                <select value={store.handlingTime} onChange={(e) => store.setHandlingTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="">Select…</option>
                  {HANDLING_TIMES.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>

            {/* Returns */}
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Accept returns</label>
                  <p className="text-[11px] text-gray-400 mt-0.5">Buyers prefer listings with returns</p>
                </div>
                <button onClick={() => store.setAcceptReturns(!store.acceptReturns)}
                  className={clsx('relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    store.acceptReturns ? 'bg-blue-600' : 'bg-gray-300')}>
                  <span className={clsx('inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow',
                    store.acceptReturns ? 'translate-x-[24px]' : 'translate-x-[4px]')} />
                </button>
              </div>
              {store.acceptReturns && (
                <div className="flex gap-1.5">
                  {RETURN_WINDOWS.map((d) => (
                    <button key={d} onClick={() => store.setReturnWindow(d)}
                      className={clsx('flex-1 py-1.5 rounded-lg border-2 text-xs font-semibold transition-colors',
                        store.returnWindow === d ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                      {d}-day
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* SECTION 3: Total cost summary                                       */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {(binPrice > 0 || bidPrice > 0) && (
        <section className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Buyer pays</p>
          <div className="space-y-2">
            {(store.listingType === 'BUY_IT_NOW' || store.listingType === 'AUCTION_BIN') && binPrice > 0 && (
              <div className="flex items-baseline justify-between text-sm">
                <div className="flex items-center gap-3 text-gray-600">
                  <span className="font-medium">Buy It Now ${binPrice.toFixed(2)}</span>
                  <span className="text-gray-300">+</span>
                  <span>Shipping {isFreeShipping ? <span className="text-green-600 font-semibold">FREE</span> : shipCost > 0 ? `$${shipCost.toFixed(2)}` : '—'}</span>
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  ${(binPrice + shipCost).toFixed(2)}
                </span>
              </div>
            )}
            {(store.listingType === 'AUCTION' || store.listingType === 'AUCTION_BIN') && bidPrice > 0 && (
              <div className="flex items-baseline justify-between text-sm">
                <div className="flex items-center gap-3 text-gray-600">
                  <span className="font-medium">Starting bid ${bidPrice.toFixed(2)}</span>
                  <span className="text-gray-300">+</span>
                  <span>Shipping {isFreeShipping ? <span className="text-green-600 font-semibold">FREE</span> : shipCost > 0 ? `$${shipCost.toFixed(2)}` : '—'}</span>
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  ${(bidPrice + shipCost).toFixed(2)}<span className="text-sm font-normal text-gray-400 ml-1">min</span>
                </span>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
